/**
 * What an event of the staff stream makes stale on the first screen.
 *
 * The screen is counts, and nearly every event moves one of them. Counting
 * costs the services a query each time, so the screen is read again at most
 * once in a couple of seconds, however many events come.
 */
import type { LiveEvent, Refresh } from "@/shared/live";

import { MONEY_KEY, NOW_KEY } from "./queries";

/** At most once in this long: what is going on. */
export const OVERVIEW_DELAY_MS = 2_000;
/** And the money: Billing settles a visit a moment after Order says it is done. */
export const MONEY_DELAY_MS = 5_000;

/** Events that move no count of the screen. */
const SILENT: ReadonlySet<string> = new Set([
  "visit.offered",
  "visit.offer_closed",
  "visit.rated",
  "visit.rating_moderated",
]);

/** Events after which the ledger reads differently: a visit settled, or taken back. */
const MOVES_MONEY: ReadonlySet<string> = new Set([
  "visit.completed",
  "visit.report_confirmed",
  "visit.dispute_closed",
]);

export function nowLive(event: LiveEvent, refresh: Refresh): void {
  // A message in a support thread: the count of the threads owed a reply moves.
  if (event.type === "support.message_created") {
    refresh([...NOW_KEY, "support"]);
    return;
  }
  // A wallet moved: the ledger's block counts again (its own pace: a burst is one).
  if (event.type === "wallet.changed") {
    refresh(MONEY_KEY, MONEY_DELAY_MS);
    return;
  }
  // An adjustment began to wait for a second person, or was decided: the
  // block's count of them moves. One at a time, as people press buttons.
  if (event.type === "adjustment.requested" || event.type === "adjustment.decided") {
    refresh(MONEY_KEY);
    return;
  }
  // The queue moved: who has a row, or a row left it.
  if (event.type.startsWith("escalation.")) {
    refresh([...NOW_KEY, "overview"], OVERVIEW_DELAY_MS);
    return;
  }
  if (!event.type.startsWith("visit.") && !event.type.startsWith("specialist.")) return;
  if (event.type === "specialist.position" || SILENT.has(event.type)) return;
  refresh([...NOW_KEY, "overview"], OVERVIEW_DELAY_MS);
  if (event.type === "specialist.created" || event.type === "specialist.status_changed") {
    refresh([...NOW_KEY, "applications"], OVERVIEW_DELAY_MS);
  }
  if (MOVES_MONEY.has(event.type)) refresh(MONEY_KEY, MONEY_DELAY_MS);
}
